import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Edit2, Trash2, Eye, EyeOff, Send, 
  Image, Link as LinkIcon, Video, Bold, Italic, List, 
  Heading1, Heading2, Save, X, Users, Mail
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || '';

const AdminBlogPage = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [showSubscribers, setShowSubscribers] = useState(false);
  
  // Editor state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [published, setPublished] = useState(false);
  const [sendNewsletter, setSendNewsletter] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const contentRef = useRef(null);

  useEffect(() => {
    fetchBlogs();
    fetchSubscribers();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/blog`);
      setBlogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/subscribers`);
      setSubscribers(response.data.subscribers || []);
    } catch (error) {
      console.error('Failed to fetch subscribers');
    }
  };

  const resetEditor = () => {
    setTitle('');
    setContent('');
    setExcerpt('');
    setCoverImage('');
    setPublished(false);
    setSendNewsletter(false);
    setEditingBlog(null);
  };

  const openEditor = (blog = null) => {
    if (blog) {
      setTitle(blog.title);
      setContent(blog.content);
      setExcerpt(blog.excerpt || '');
      setCoverImage(blog.cover_image_url || '');
      setPublished(blog.published);
      setEditingBlog(blog);
    } else {
      resetEditor();
    }
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    resetEditor();
  };

  const handleImageUpload = async (e, forCover = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/api/admin/image/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = `${BACKEND_URL}${response.data.image_url}`;
      
      if (forCover) {
        setCoverImage(imageUrl);
        toast.success('Cover image uploaded');
      } else {
        // Insert image into content
        const imgHtml = `<img src="${imageUrl}" alt="Blog image" class="my-4 rounded-lg max-w-full" />`;
        setContent(prev => prev + imgHtml);
        toast.success('Image inserted');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    const text = prompt('Enter link text:');
    if (url && text) {
      const linkHtml = `<a href="${url}" target="_blank" class="text-cyan-600 hover:underline">${text}</a>`;
      setContent(prev => prev + linkHtml);
    }
  };

  const insertVideo = () => {
    const url = prompt('Enter YouTube or video URL:');
    if (url) {
      let embedUrl = url;
      // Convert YouTube watch URLs to embed URLs
      if (url.includes('youtube.com/watch')) {
        const videoId = url.split('v=')[1]?.split('&')[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1]?.split('?')[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }
      
      const videoHtml = `<div class="my-4 aspect-video"><iframe src="${embedUrl}" class="w-full h-full rounded-lg" frameborder="0" allowfullscreen></iframe></div>`;
      setContent(prev => prev + videoHtml);
      toast.success('Video embedded');
    }
  };

  const insertHeading = (level) => {
    const text = prompt(`Enter heading text:`);
    if (text) {
      const headingHtml = level === 1 
        ? `<h2 class="text-2xl font-bold mt-6 mb-3">${text}</h2>`
        : `<h3 class="text-xl font-semibold mt-4 mb-2">${text}</h3>`;
      setContent(prev => prev + headingHtml);
    }
  };

  const wrapSelection = (tag) => {
    const text = prompt(`Enter ${tag} text:`);
    if (text) {
      if (tag === 'bold') {
        setContent(prev => prev + `<strong>${text}</strong>`);
      } else if (tag === 'italic') {
        setContent(prev => prev + `<em>${text}</em>`);
      } else if (tag === 'list') {
        setContent(prev => prev + `<ul class="list-disc ml-6 my-3"><li>${text}</li></ul>`);
      }
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!content.trim()) {
      toast.error('Please enter some content');
      return;
    }

    setSaving(true);
    try {
      if (editingBlog) {
        await axios.put(`${API}/api/admin/blog/${editingBlog.blog_id}`, {
          title,
          content,
          excerpt: excerpt || content.replace(/<[^>]*>/g, '').substring(0, 200) + '...',
          cover_image_url: coverImage || null,
          published
        });
        toast.success('Blog post updated');
      } else {
        await axios.post(`${API}/api/admin/blog`, {
          title,
          content,
          excerpt: excerpt || content.replace(/<[^>]*>/g, '').substring(0, 200) + '...',
          cover_image_url: coverImage || null,
          published,
          send_newsletter: sendNewsletter && published
        });
        toast.success(sendNewsletter && published ? 'Blog published & newsletter sent!' : 'Blog post created');
      }
      
      closeEditor();
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to save blog post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/blog/${blogId}`);
      toast.success('Blog post deleted');
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const handleSendNewsletter = async (blogId) => {
    if (!window.confirm(`Send this blog post to ${subscribers.length} subscribers?`)) return;
    
    try {
      const response = await axios.post(`${API}/api/admin/blog/${blogId}/send-newsletter`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to send newsletter');
    }
  };

  const togglePublish = async (blog) => {
    try {
      await axios.put(`${API}/api/admin/blog/${blog.blog_id}`, {
        published: !blog.published
      });
      toast.success(blog.published ? 'Blog unpublished' : 'Blog published');
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to update blog');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-slate-900">Blog Management</h1>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSubscribers(!showSubscribers)}
              className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-semibold"
            >
              <Users className="w-5 h-5" />
              {subscribers.length} Subscribers
            </button>
            <button
              onClick={() => openEditor()}
              className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg font-bold"
            >
              <Plus className="w-5 h-5" />
              New Post
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Subscribers Panel */}
        {showSubscribers && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">Newsletter Subscribers</h2>
              <button onClick={() => setShowSubscribers(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {subscribers.length === 0 ? (
              <p className="text-slate-500">No subscribers yet. Share your blog to grow your newsletter list!</p>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                {subscribers.map((sub, idx) => (
                  <div key={idx} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{sub.email}</span>
                    {sub.name && <span className="text-slate-400 text-sm">({sub.name})</span>}
                    {sub.subscribed_at && (
                      <span className="text-slate-300 text-xs ml-auto">
                        {new Date(sub.subscribed_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Blog List */}
        {blogs.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">📝</div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">No blog posts yet</h2>
            <p className="text-slate-500 mb-6">Create your first blog post to share with your audience</p>
            <button
              onClick={() => openEditor()}
              className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-bold"
            >
              Create First Post
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {blogs.map((blog) => (
              <div
                key={blog.blog_id}
                className="bg-white rounded-xl shadow-lg p-6 flex items-start gap-4"
              >
                {blog.cover_image_url ? (
                  <img
                    src={blog.cover_image_url}
                    alt={blog.title}
                    className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-3xl">📝</span>
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">{blog.title}</h3>
                      <p className="text-slate-500 text-sm mb-2">{formatDate(blog.created_at)}</p>
                      <p className="text-slate-600 text-sm line-clamp-2">{blog.excerpt}</p>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        blog.published 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {blog.published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4">
                    <button
                      onClick={() => openEditor(blog)}
                      className="flex items-center gap-1 text-slate-600 hover:text-cyan-600 text-sm font-semibold"
                    >
                      <Edit2 className="w-4 h-4" /> Edit
                    </button>
                    <button
                      onClick={() => togglePublish(blog)}
                      className="flex items-center gap-1 text-slate-600 hover:text-cyan-600 text-sm font-semibold"
                    >
                      {blog.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {blog.published ? 'Unpublish' : 'Publish'}
                    </button>
                    {blog.published && (
                      <button
                        onClick={() => handleSendNewsletter(blog.blog_id)}
                        className="flex items-center gap-1 text-slate-600 hover:text-cyan-600 text-sm font-semibold"
                      >
                        <Send className="w-4 h-4" /> Send Newsletter
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(blog.blog_id)}
                      className="flex items-center gap-1 text-red-500 hover:text-red-700 text-sm font-semibold ml-auto"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4">
            {/* Editor Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-bold text-slate-900">
                {editingBlog ? 'Edit Blog Post' : 'New Blog Post'}
              </h2>
              <button onClick={closeEditor} className="text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Editor Content */}
            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter blog title..."
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 text-lg font-semibold"
                />
              </div>

              {/* Cover Image */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Cover Image</label>
                {coverImage ? (
                  <div className="relative">
                    <img src={coverImage} alt="Cover" className="w-full h-48 object-cover rounded-lg" />
                    <button
                      onClick={() => setCoverImage('')}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-cyan-500">
                    <div className="text-center">
                      <Image className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <span className="text-slate-500">Click to upload cover image</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, true)}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Excerpt (optional - auto-generated if empty)
                </label>
                <textarea
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="Brief summary of your post..."
                  rows={2}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
                />
              </div>

              {/* Content Editor */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Content</label>
                
                {/* Toolbar */}
                <div className="flex items-center gap-1 p-2 bg-slate-100 rounded-t-lg border border-b-0 border-slate-300 flex-wrap">
                  <button
                    onClick={() => insertHeading(1)}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Heading 1"
                  >
                    <Heading1 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => insertHeading(2)}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Heading 2"
                  >
                    <Heading2 className="w-5 h-5" />
                  </button>
                  <div className="w-px h-6 bg-slate-300 mx-1" />
                  <button
                    onClick={() => wrapSelection('bold')}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Bold"
                  >
                    <Bold className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => wrapSelection('italic')}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Italic"
                  >
                    <Italic className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => wrapSelection('list')}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="List"
                  >
                    <List className="w-5 h-5" />
                  </button>
                  <div className="w-px h-6 bg-slate-300 mx-1" />
                  <label className="p-2 hover:bg-slate-200 rounded cursor-pointer" title="Insert Image">
                    <Image className="w-5 h-5" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, false)}
                      className="hidden"
                    />
                  </label>
                  <button
                    onClick={insertLink}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Insert Link"
                  >
                    <LinkIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={insertVideo}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Embed Video"
                  >
                    <Video className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Content textarea */}
                <textarea
                  ref={contentRef}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Write your blog post content here... (HTML supported)"
                  rows={12}
                  className="w-full px-4 py-3 rounded-b-lg border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 font-mono text-sm"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Tip: Use the toolbar to insert images, links, videos, and formatting. HTML is supported.
                </p>
              </div>

              {/* Publish Options */}
              <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={published}
                    onChange={(e) => setPublished(e.target.checked)}
                    className="w-5 h-5 rounded border-slate-300 text-cyan-500 focus:ring-cyan-500"
                  />
                  <span className="font-semibold text-slate-700">Publish immediately</span>
                </label>
                
                {!editingBlog && published && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendNewsletter}
                      onChange={(e) => setSendNewsletter(e.target.checked)}
                      className="w-5 h-5 rounded border-slate-300 text-cyan-500 focus:ring-cyan-500"
                    />
                    <span className="font-semibold text-slate-700">
                      Send as newsletter to {subscribers.length} subscribers
                    </span>
                  </label>
                )}
              </div>
            </div>

            {/* Editor Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-slate-50 rounded-b-2xl">
              <button
                onClick={closeEditor}
                className="px-6 py-2 text-slate-600 hover:text-slate-900 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-cyan-300 text-white px-6 py-2 rounded-lg font-bold"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {editingBlog ? 'Update' : 'Save'} Post
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlogPage;
